import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';
import { useAuthStore } from '@store/authStore';
import { ROUTES } from '@/constants';
import { Skeleton } from '@components/ui/Skeleton';
import ClientCard from './components/ClientCard';
import { EmptyState } from '@assets/images';

import {
  RiSearchLine,
  RiFilter3Line,
  RiArrowUpDownLine,
  RiUserAddLine,
  RiUserLine,
} from '@remixicon/react';

const Clients = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuthStore();
  const [search, setSearch] = useState('');

  const { data: clients = [], isLoading } = useQuery({
    queryKey: ['clients', userProfile?.userprofile_id],
    queryFn: () => apiClient.getClients(0, 200, { include: 'quotes,quotes.bids', broker_id: userProfile?.userprofile_id }),
    staleTime: 30000,
  });

  const filteredClients = useMemo(() => {
    let result = [...clients];
    if (search.trim()) {
      const q = search.toLowerCase().trim();
      result = result.filter((c) =>
        c.first_name?.toLowerCase().includes(q) ||
        c.last_name?.toLowerCase().includes(q) ||
        c.company_name?.toLowerCase().includes(q) ||
        c.company?.toLowerCase().includes(q) ||
        c.email?.toLowerCase().includes(q)
      );
    }
    // Sort: clients with quotes first, then by latest quote date descending
    result.sort((a, b) => {
      const aHasQuotes = a.quotes?.length > 0 ? 1 : 0;
      const bHasQuotes = b.quotes?.length > 0 ? 1 : 0;
      if (aHasQuotes !== bHasQuotes) return bHasQuotes - aHasQuotes;
      if (aHasQuotes && bHasQuotes) {
        const aLatest = Math.max(...a.quotes.map((q) => new Date(q.created_at).getTime()));
        const bLatest = Math.max(...b.quotes.map((q) => new Date(q.created_at).getTime()));
        return bLatest - aLatest;
      }
      return 0;
    });
    return result;
  }, [clients, search]);

  return (
    <div className="pb-8 max-h-[calc(100vh-12rem)] overflow-y-auto pr-2 [&::-webkit-scrollbar]:w-[6px] [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-200 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-gray-300">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          {isLoading ? '' : `${filteredClients.length} `}Clients
        </h1>

        <div className="flex items-center gap-3">
          {/* Search */}
          <div className="relative">
            <RiSearchLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-4 py-2 w-68 rounded-full text-sm focus:outline-none"
            />
          </div>

          {/* Filter */}
          <button className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-white text-sm text-gray-700 transition-colors">
            <RiFilter3Line className="w-4 h-4" />
            Filter
          </button>

          {/* Sort by */}
          <button className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-white text-sm text-gray-700 transition-colors">
            <RiArrowUpDownLine className="w-4 h-4" />
            Sort by
          </button>

          {/* Add client */}
          <button
            onClick={() => navigate(ROUTES.ADD_CLIENT.path)}
            className="flex items-center gap-1.5 px-3 py-2 bg-white text-gray-700 rounded-full text-sm font-medium transition-colors"
          >
            <RiUserAddLine className="w-4 h-4" />
            Add client
          </button>
        </div>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-4 w-60" />
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-8 w-full" />
            </div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && filteredClients.length === 0 && (
        <div className="text-center py-16">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <img src={EmptyState} alt="No clients" className="w-30 h-30 mx-auto" />
          </div>
          <h3 className="text-md font-semibold text-gray-900 mb-2">
            {search ? 'No clients found' : 'No clients enrolled yet'}
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            {search ? 'Try adjusting your search query.' : ''}
          </p>
          {!search && (
            <button
              onClick={() => navigate(ROUTES.ADD_CLIENT.path)}
              className="inline-flex items-center gap-1.5 px-4 py-2 !bg-white !border border-primary-500 text-primary-500 rounded-full text-sm font-medium transition-colors hover:bg-gray-800"
            >
              <RiUserAddLine className="w-4 h-4" />
              Add Client
            </button>
          )}
        </div>
      )}

      {/* Client cards grid */}
      {!isLoading && filteredClients.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredClients.map((client) => (
            <ClientCard
              key={client.client_id || client.id}
              client={client}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Clients;
